/**
 * Contract Filters State Management Hook
 * US-CONT-005: View Expiring Contracts Alert
 * Manages filter, sort, and pagination state
 */

import { useState, useCallback, useMemo } from 'react';
import type {
  ContractFilters,
  ContractSort,
  PaginationConfig,
  FilterState,
  ContractUrgency,
  ContractType,
  ContractStatus
} from '../types/expiringContracts.types';
import { DEFAULT_SORT, DEFAULT_PAGE_SIZE, DEFAULT_DAYS_THRESHOLD } from '../constants/expiringContracts.constants';

/**
 * Initial filter state
 */
const INITIAL_FILTERS: ContractFilters = {
  search: '',
  urgency: [],
  contractType: [],
  status: [],
  startDate: undefined,
  endDate: undefined
};

/**
 * Initial pagination state
 */
const INITIAL_PAGINATION: PaginationConfig = {
  page: 0,
  size: DEFAULT_PAGE_SIZE
};

/**
 * Hook to manage contract filters, sort, and pagination
 * @returns Filter management functions and state
 */
export function useContractFilters() {
  const [filters, setFilters] = useState<ContractFilters>(INITIAL_FILTERS);
  const [sort, setSort] = useState<ContractSort>(DEFAULT_SORT);
  const [pagination, setPagination] = useState<PaginationConfig>(INITIAL_PAGINATION);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return (
      !!filters.search ||
      (filters.urgency && filters.urgency.length > 0) ||
      (filters.contractType && filters.contractType.length > 0) ||
      (filters.status && filters.status.length > 0) ||
      !!filters.startDate ||
      !!filters.endDate
    );
  }, [filters]);

  /**
   * Get complete filter state
   */
  const filterState: FilterState = useMemo(() => {
    return {
      ...filters,
      ...sort,
      ...pagination,
      hasActiveFilters
    };
  }, [filters, sort, pagination, hasActiveFilters]);

  /**
   * Update filters (partial update)
   */
  const updateFilters = useCallback((updates: Partial<ContractFilters>) => {
    setFilters(prev => ({ ...prev, ...updates }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Update search query
   */
  const setSearch = useCallback((search: string) => {
    updateFilters({ search });
  }, [updateFilters]);

  /**
   * Update urgency filter
   */
  const setUrgency = useCallback((urgency: ContractUrgency[]) => {
    updateFilters({ urgency });
  }, [updateFilters]);

  /**
   * Toggle urgency filter
   */
  const toggleUrgency = useCallback((urgency: ContractUrgency) => {
    setFilters(prev => {
      const current = prev.urgency || [];
      const updated = current.includes(urgency)
        ? current.filter(u => u !== urgency)
        : [...current, urgency];
      return { ...prev, urgency: updated };
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Update contract type filter
   */
  const setContractType = useCallback((contractType: ContractType[]) => {
    updateFilters({ contractType });
  }, [updateFilters]);

  /**
   * Toggle contract type filter
   */
  const toggleContractType = useCallback((type: ContractType) => {
    setFilters(prev => {
      const current = prev.contractType || [];
      const updated = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return { ...prev, contractType: updated };
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Update status filter
   */
  const setStatus = useCallback((status: ContractStatus[]) => {
    updateFilters({ status });
  }, [updateFilters]);

  /**
   * Update date range filter
   */
  const setDateRange = useCallback((startDate?: string, endDate?: string) => {
    updateFilters({ startDate, endDate });
  }, [updateFilters]);

  /**
   * Update sort configuration
   */
  const updateSort = useCallback((updates: Partial<ContractSort>) => {
    setSort(prev => ({ ...prev, ...updates }));
    // Reset to first page when sort changes
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Toggle sort direction for a field
   */
  const toggleSortDirection = useCallback((field: ContractSort['field']) => {
    setSort(prev => {
      if (prev.field === field) {
        // Toggle direction if same field
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // New field, default to ascending
      return {
        field,
        direction: 'asc'
      };
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Update pagination configuration
   */
  const updatePagination = useCallback((updates: Partial<PaginationConfig>) => {
    setPagination(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Go to specific page
   */
  const gotoPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  /**
   * Go to next page
   */
  const nextPage = useCallback(() => {
    setPagination(prev => ({ ...prev, page: prev.page + 1 }));
  }, []);

  /**
   * Go to previous page
   */
  const previousPage = useCallback(() => {
    setPagination(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }));
  }, []);

  /**
   * Update page size
   */
  const setPageSize = useCallback((size: number) => {
    setPagination({ page: 0, size });
  }, []);

  /**
   * Reset all filters to initial state
   */
  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setSort(DEFAULT_SORT);
    setPagination(INITIAL_PAGINATION);
  }, []);

  /**
   * Reset only filters, keep sort and pagination
   */
  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  return {
    // State
    filters,
    sort,
    pagination,
    filterState,
    hasActiveFilters,

    // Filter updates
    updateFilters,
    setSearch,
    setUrgency,
    toggleUrgency,
    setContractType,
    toggleContractType,
    setStatus,
    setDateRange,

    // Sort updates
    updateSort,
    toggleSortDirection,

    // Pagination updates
    updatePagination,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    // Reset functions
    resetFilters,
    clearFilters
  };
}

/**
 * Hook to get query parameters for API calls
 * Combines filters, sort, and pagination into a single object
 * @param filterState - Current filter state
 * @returns Query parameters object
 */
export function useContractQueryParams(filterState: FilterState) {
  return useMemo(() => {
    return {
      // Filters
      search: filterState.search || undefined,
      urgency: filterState.urgency && filterState.urgency.length > 0
        ? filterState.urgency
        : undefined,
      contractType: filterState.contractType && filterState.contractType.length > 0
        ? filterState.contractType
        : undefined,
      status: filterState.status && filterState.status.length > 0
        ? filterState.status
        : undefined,
      startDate: filterState.startDate || undefined,
      endDate: filterState.endDate || undefined,
      days: DEFAULT_DAYS_THRESHOLD,

      // Sort
      field: filterState.field,
      direction: filterState.direction,

      // Pagination
      page: filterState.page,
      size: filterState.size
    };
  }, [filterState]);
}
